import {
  AnyAction,
  Store,
  StoreEnhancer,
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
} from 'redux';
import {
  RouterState,
  connectRouter,
  routerMiddleware,
} from 'connected-react-router';
import { History, createBrowserHistory } from 'history';
import thunk, { ThunkAction } from 'redux-thunk';
import { ActionType } from 'typesafe-actions';

import * as Api from '../services/apiClient';
import * as Fetch from '../services/fetchClient';
import { ApiState, api } from './api/api.reducer';
import { apiActions } from './api/api.actions';
import { fixturesActions as apiFixturesActions } from './fixtures/fixtures.actions';
import { user, UserState } from './user/user.reducer';
import { assortment, AssortmentState } from './assortment/assortment.reducer';
import {
  optimizationConfiguration,
  OptimizationConfigurationState,
} from './optimizationConfiguration/optimizationConfiguration.reducer';
import { job, planogramFilter, metricJob, solverOutput, blockingAttributesFilter } from './job/job.reducer';
import { jobs } from './jobs/jobs.reducer';
import type {
  JobState,
  PlanogremFilterState,
  MetricJobState,
  SolverOutputState,
  BlockingAttributesI,
} from './job/job.types';
import { comparison, ComparisonState } from './comparison/comparison.reducer';
import { getConfig } from '../utils/getConfig';
import { reviewAndEdit, ReviewAndEditState } from './viewAndEdit/viewAndEdit.reducer';
import { notification, NotificationState } from './notifications/notifications.reducer';
import { workFlow } from './workFlow/workFlow.reducer';
import { WorkFlowState } from './workFlow/workFlow.types';
import { globalState } from './tetrisGlobal/tetrisGlobal.reducer';
import { TetrisGlobalState } from './tetrisGlobal/tetrisGlobal.types';
import { ProtoLinearState } from './protoLinear/protoLinear.types';
import { protoLinearList } from './protoLinear/protoLinear.reducer';
import { VendorPegboardState } from './vendorPegboard/vendorPegboard.types';
import { vendorPegboard } from './vendorPegboard/vendorPegboard.reducer';
import { fixtures, fixturesNotchesHoles } from './fixtures/fixtures.reducer';
import type { FixturesState, FixturesNotchesHolesState } from './fixtures/fixtures.types';
import { connectInterceptions } from '../services/axiosInterceptor/axiosInterceptor';
import { ApiResponseState, axiosInterception } from './axiosInterceptor/axiosInterceptor.reducer';
import { seeds } from './seeds/seeds.reducers';
import { SeedsState } from './seeds/seeds.types';
import { merchGuide } from './merchGuide/merchGuide.reducer';
import { MerchGuideState } from './merchGuide/merchGuide.types';
import { JobsState } from './jobs/jobs.types';
import { createTelemetryMiddleware } from '../telemetry/middleware';

const agregatedActions = {
  ...apiActions,
  ...apiFixturesActions,
};

export type Actions = ActionType<typeof agregatedActions>;

export interface ThunkExtraArgument {
  apiClientJava: Api.ApiClient;
  fetchClientJava: Fetch.FetchClient;
}

type ThunkResult<R> = ThunkAction<R, StoreState, ThunkExtraArgument, Actions>;

export type ThunkActionCreator<P = void, R = void> = (
  params: P
) => ThunkResult<R>;

export interface StoreState {
  api: ApiState;
  axiosInterception: ApiResponseState;
  assortment: AssortmentState;
  comparison: ComparisonState;
  user: UserState;
  router: RouterState;
  notification: NotificationState;
  optimizationConfiguration: OptimizationConfigurationState;
  reviewAndEdit: ReviewAndEditState;
  jobs: JobsState;
  job: JobState;
  planogramFilter: PlanogremFilterState;
  metricJob: MetricJobState,
  solverOutput: SolverOutputState,
  workFlow: WorkFlowState,
  globalState: TetrisGlobalState,
  protoLinearList: ProtoLinearState,
  vendorPegboard: VendorPegboardState,
  fixtures: FixturesState,
  fixturesNotchesHoles: FixturesNotchesHolesState,
  blockingAttributesFilter: BlockingAttributesI,
  seeds: SeedsState,
  merchGuide: MerchGuideState,
}

interface InitStore {
  store: Store<StoreState>;
  history: History;
}

const BACKEND_JAVA_URL = getConfig('REACT_APP_BACKEND_JAVA_URL');
const BACKEND_JAVA_URL_EXT = getConfig('REACT_APP_BACKEND_JAVA_URL_EXT');

// maybe we can move this function to some helpers file
export const getJavaInstance = () => {
  const searchHost = window.location.hostname;

  if (searchHost.includes('corp.ad.ctc')) {
    if (searchHost.includes('agw')) { // agw
      return BACKEND_JAVA_URL_EXT;
    } else {
      return BACKEND_JAVA_URL;
    }
  } else if (searchHost.includes('cantire.com')) { // external
    return BACKEND_JAVA_URL_EXT;
  } else {
    return BACKEND_JAVA_URL;
  }
};
//maybe we can move this function to some helpers file

const axiosInstanceJava = Api.createAxiosInstance(getJavaInstance());

export const apiClientJava = new Api.ApiClient(axiosInstanceJava);
export const fetchClientJava = new Fetch.FetchClient(getJavaInstance());

export function initStore(): InitStore {
  const enhancers: Array<StoreEnhancer> = [];

  const publicURL = process.env.PUBLIC_URL;
  const history = createBrowserHistory({
    basename: publicURL,
  });

  const middlewares = [
    routerMiddleware(history),
    thunk.withExtraArgument({ apiClientJava, fetchClientJava }),
    createTelemetryMiddleware(),
  ];

  if (
    process.env.NODE_ENV !== 'production' &&
    window.__REDUX_DEVTOOLS_EXTENSION__
  ) {
    enhancers.push(window.__REDUX_DEVTOOLS_EXTENSION__({ maxAge: 200 }));
  }

  const store = createStore<StoreState, AnyAction, any, any>(
    combineReducers({
      api,
      axiosInterception,
      assortment,
      comparison,
      user,
      notification,
      optimizationConfiguration,
      reviewAndEdit,
      router: connectRouter(history),
      jobs,
      job,
      planogramFilter,
      metricJob,
      solverOutput,
      workFlow,
      globalState,
      protoLinearList,
      vendorPegboard,
      fixtures,
      fixturesNotchesHoles,
      blockingAttributesFilter,
      seeds,
      merchGuide,
    }),
    compose(applyMiddleware(...middlewares), ...enhancers)
  );

  connectInterceptions(axiosInstanceJava, store);

  return {
    store,
    history,
  };
}
